/**
 * Domain configuration for TeamRezy
 * Handles different environments and subdomain routing
 */

import { log } from '../utils/logger';

const getDomainConfig = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;
  
  // Local development configuration
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return {
      environment: 'development',
      baseDomain: `${hostname}${port ? `:${port}` : ''}`,
      marketingUrl: `${protocol}//${hostname}${port ? `:${port}` : ''}`,
      appUrl: `${protocol}//${hostname}${port ? `:${port}` : ''}?subdomain=app`,
      apiUrl: import.meta.env.VITE_SUPABASE_URL || 'http://localhost:54321',
      isLocal: true
    };
  }
  
  // Preview/Staging environment
  if (hostname.includes('preview') || hostname.includes('staging') || hostname.includes('test')) {
    // For preview.teamrezy.com, we want the app to be shown directly
    // No need for separate marketing/app URLs in preview
    const baseDomain = hostname;
    return {
      environment: hostname.includes('preview') ? 'preview' : 'staging',
      baseDomain,
      marketingUrl: `${protocol}//${baseDomain}`, // Same as app for preview
      appUrl: `${protocol}//${baseDomain}`, // Preview shows app directly
      apiUrl: import.meta.env.VITE_SUPABASE_URL,
      isLocal: false,
      isPreview: hostname.includes('preview')
    };
  }
  
  // Production environment
  const baseDomain = hostname.replace(/^(app|www)\./, '');
  return {
    environment: 'production',
    baseDomain,
    marketingUrl: `${protocol}//www.${baseDomain}`,
    appUrl: `${protocol}//app.${baseDomain}`,
    apiUrl: import.meta.env.VITE_SUPABASE_URL,
    isLocal: false
  };
};

/**
 * Check if current domain is the app subdomain
 */
export const isAppDomain = () => {
  // Check for environment variable override first
  const siteMode = import.meta.env.VITE_SITE_MODE;
  if (siteMode === 'app') return true;
  if (siteMode === 'marketing') return false;

  const hostname = window.location.hostname;
  const urlParams = new URLSearchParams(window.location.search);

  // Check for URL parameter override (works on any domain)
  const viewParam = urlParams.get('view');
  if (viewParam === 'marketing') return false;
  if (viewParam === 'app') return true;

  // For local development, check subdomain parameter
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const subdomainParam = urlParams.get('subdomain');
    const isApp = subdomainParam === 'app';

    // Log for debugging
    log.debug('🔍 Subdomain Detection:', {
      hostname,
      subdomainParam,
      isApp,
      fullUrl: window.location.href
    });

    return isApp;
  }
  
  // Special handling for preview/staging environments
  // These should show the app by default, but allow marketing override
  if (hostname === 'preview.teamrezy.com' ||
      hostname === 'staging.teamrezy.com' ||
      hostname.includes('preview.') ||
      hostname.includes('staging.')) {

    // Allow override to show marketing page with ?view=marketing
    const viewParam = urlParams.get('view');
    if (viewParam === 'marketing') {
      return false; // Show marketing site
    }

    return true; // Show app on preview/staging domains by default
  }
  
  // For production, check if hostname starts with 'app.'
  return hostname.startsWith('app.');
};

/**
 * Get the appropriate redirect URL for authentication
 */
export const getAuthRedirectUrl = () => {
  const config = getDomainConfig();
  return config.appUrl;
};

/**
 * Redirect to app domain
 */
export const redirectToApp = (path = '/') => {
  const config = getDomainConfig();
  const url = config.isLocal 
    ? `${config.appUrl}${path}`.replace('?subdomain=app/', '?subdomain=app&path=')
    : `${config.appUrl}${path}`;
  
  window.location.href = url;
};

/**
 * Redirect to marketing domain
 */
export const redirectToMarketing = (path = '/') => {
  const config = getDomainConfig();
  const hostname = window.location.hostname;

  // For preview/staging environments, use ?view=marketing parameter
  if (hostname === 'preview.teamrezy.com' ||
      hostname === 'staging.teamrezy.com' ||
      hostname.includes('preview.') ||
      hostname.includes('staging.')) {
    const currentOrigin = window.location.origin;
    window.location.href = `${currentOrigin}${path}?view=marketing`;
  } else {
    window.location.href = `${config.marketingUrl}${path}`;
  }
};

export default getDomainConfig;